import { useCallback, useEffect } from 'react';
import { useStore } from '@/store/store';
import { useDebounce } from '@/hooks/use-debounce';

/** Match `useDeviceOrientation` default so resize-driven layout work stays bounded during drag-resize. */
const RESIZE_DEBOUNCE_MS = 100;

export const useIsSearchOpen = () => {
    const isSearchOpen = useStore((s) => s.search.isSearchOpen);
    const isSearchRedesignOn = useStore((s) => s.search.isSearchRedesignVariant);

    const syncNavWidthToDrawerLeft = useCallback(() => {
        const drawer = document.querySelector<HTMLElement>('.search-drawer');
        const nav = document.querySelector<HTMLElement>('.header-fixed nav');
        if (isSearchRedesignOn && isSearchOpen && drawer && nav) {
            const drawerWidthPx = drawer.getBoundingClientRect().width;
            const drawerLeftFromViewport = window.innerWidth - drawerWidthPx;
            const navLeftFromViewport = nav.getBoundingClientRect().left;
            const widthPx = drawerLeftFromViewport - navLeftFromViewport;
            nav.style.width = `${Math.max(0, widthPx)}px`;
        } else if (nav) {
            nav.style.removeProperty('width');
        }
    }, [isSearchRedesignOn, isSearchOpen]);

    const debouncedSyncNavWidthToDrawerLeft = useDebounce(syncNavWidthToDrawerLeft, RESIZE_DEBOUNCE_MS);

    useEffect(() => {
        if (isSearchOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }

        // add a temporary class to the body to update the search design. Will be removed at cleanup
        if (isSearchOpen && isSearchRedesignOn) {
            document.body.classList.add('search-open-redesign');
        } else {
            document.body.classList.remove('search-open-redesign');
        }

        if (isSearchRedesignOn) {
            // get measurments right away, even if search is not open yet
            syncNavWidthToDrawerLeft();

            if (isSearchOpen) {
                requestAnimationFrame(() => {
                    // sync again on the next effect run
                    requestAnimationFrame(syncNavWidthToDrawerLeft);
                });
            }
        }

        let detachResize: (() => void) | undefined;

        if (isSearchRedesignOn && isSearchOpen) {
            window.addEventListener('resize', debouncedSyncNavWidthToDrawerLeft);
            detachResize = () => {
                window.removeEventListener('resize', debouncedSyncNavWidthToDrawerLeft);
            };
        }

        return () => {
            document.body.classList.remove('no-scroll');
            document.body.classList.remove('search-open-redesign');
            detachResize?.();
            document.querySelector<HTMLElement>('.header-fixed nav')?.style.removeProperty('width');
        };
    }, [isSearchOpen, isSearchRedesignOn, syncNavWidthToDrawerLeft, debouncedSyncNavWidthToDrawerLeft]);
};
